import { useState, type FormEvent } from "react";
import { useSession } from "../auth/SessionProvider";
import { useSaveExperience } from "../platform/api/queries";
import {
  presetFor,
  PROFILE_MODULES,
  type ProfessionalPreset,
  type ProfileModuleId,
  type UsageMode,
} from "../platform/model/access";
import { publicMessage } from "../../shared/lib/errors";
import { ThemeToggle } from "../../shared/ui/ThemeProvider";

export function OnboardingPage() {
  const { user } = useSession();
  if (user === null) throw new Error("Onboarding requer sessão");
  const currentUser = user;
  const save = useSaveExperience(currentUser.id);
  const [mode, setMode] = useState<UsageMode>("personal");
  const [professionalProfile, setProfessionalProfile] = useState<ProfessionalPreset>("coach");
  const [modules, setModules] = useState<ProfileModuleId[]>(presetFor("personal", null));
  const [message, setMessage] = useState("");

  function chooseMode(next: UsageMode) {
    setMode(next);
    setModules(presetFor(next, next === "professional" ? professionalProfile : null));
  }
  function choosePreset(next: ProfessionalPreset) {
    setProfessionalProfile(next);
    setModules(presetFor("professional", next));
  }
  function toggle(module: ProfileModuleId) {
    if (module === "assistente") return;
    setModules((current) =>
      current.includes(module) ? current.filter((item) => item !== module) : [...current, module],
    );
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    const displayName = String(
      currentUser.user_metadata.full_name ?? currentUser.user_metadata.name ?? "Meu perfil",
    );
    try {
      await save.mutateAsync({
        usageMode: mode,
        professionalProfile: mode === "professional" ? professionalProfile : null,
        defaultModules: modules,
        completeOnboarding: true,
        displayName,
      });
    } catch (error: unknown) {
      setMessage(publicMessage(error));
    }
  }
  return (
    <main className="onboarding-shell">
      <form className="onboarding-card" onSubmit={submit} aria-labelledby="onboarding-title">
        <div className="onboarding-toolbar">
          <p className="eyebrow">Configuração inicial</p>
          <ThemeToggle />
        </div>
        <h1 id="onboarding-title">Como você vai usar o Base Hormonal?</h1>
        <p>Você pode trocar o modo depois sem apagar ou migrar seus dados.</p>
        <fieldset className="choice-grid">
          <legend>Modo de uso</legend>
          <label>
            <input
              type="radio"
              name="usage-mode"
              checked={mode === "personal"}
              onChange={() => chooseMode("personal")}
            />{" "}
            <span>
              <strong>Meus próprios dados</strong>
              <small>Um único espaço chamado Meu perfil.</small>
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="usage-mode"
              checked={mode === "professional"}
              onChange={() => chooseMode("professional")}
            />{" "}
            <span>
              <strong>Alunos ou clientes</strong>
              <small>Um diretório para vários perfis.</small>
            </span>
          </label>
        </fieldset>
        {mode === "professional" ? (
          <label>
            Fluxo profissional
            <select
              aria-label="Fluxo profissional"
              value={professionalProfile}
              onChange={(event) => choosePreset(event.target.value as ProfessionalPreset)}
            >
              <option value="coach">Coach/acompanhamento</option>
              <option value="nutritionist">Nutrição</option>
              <option value="trainer">Treino</option>
              <option value="other">Outro ou multidisciplinar</option>
            </select>
            <small>O preset só sugere módulos; não valida habilitação profissional.</small>
          </label>
        ) : null}
        <fieldset className="module-grid">
          <legend>Módulos iniciais</legend>
          {PROFILE_MODULES.map((module) => (
            <label key={module.id}>
              <input
                type="checkbox"
                checked={modules.includes(module.id)}
                disabled={module.id === "assistente"}
                onChange={() => toggle(module.id)}
              />{" "}
              {module.label}
            </label>
          ))}
        </fieldset>
        <button className="primary" type="submit" disabled={save.isPending}>
          {save.isPending ? "Salvando…" : "Continuar"}
        </button>
        <p className="form-message" aria-live="polite">
          {message}
        </p>
      </form>
    </main>
  );
}
